<template>
  <div
    ref="rootEl"
    class="sdv-grid"
    :style="rootStyle"
  >
    <template
      v-for="props in internalAreas"
    >
      <slot
        name="area-container"
        v-bind="props"
      >
        <slot
          :name="props.name"
          :area="props.name"
        >
          <div
            :key="props.name"
            v-bind="props.attrs"
          >
            <slot
              :area="props.name"
            />
          </div>
        </slot>
      </slot>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType, computed, ref } from 'vue-demi'
import { LayoutGridConfig } from './types'

/**
 * Taken from https://github.com/Source-Digital/experience-engine/blob/dce46b82220b00b9305e7ba285b996f6932029a5/front/src/components/experienceLoader/use-activation-target-grid.ts#L10-L18
 * @param grid
 */
function getGridStyle (grid?: LayoutGridConfig) {
  if (grid == null) return {}
  let areas = (grid.area ?? []).map(i => i.join(' ')).map(i => `'${i}'`)
  if (areas.length <= 1) areas = areas.flat(1)
  return {
    gridGap: grid.gap != null ? `${grid.gap}` : undefined,
    gridTemplateAreas: areas.join(' '),
    gridTemplateRows: grid.rows,
    gridTemplateColumns: grid.columns
  }
}
const unique = <T>(...items: T[]): T[] => [...new Set(items)]

function getAreaList (area2d: string[][]): string[] {
  return unique(...area2d.flat(2).filter((i: string) => i != null && !['.'].includes(i)))
}

/**
 * Taken from: https://github.com/Source-Digital/experience-engine/blob/dce46b82220b00b9305e7ba285b996f6932029a5/front/src/components/experienceLoader/ExperienceLoaderV2.vue#L32-L64
 */
export default defineComponent({
  name: 'LayoutGrid',
  props: {
    grid: {
      type: Object as PropType<LayoutGridConfig>,
      required: true
    }
  },
  setup (props) {
    const rootEl = ref<HTMLElement>()
    const rootStyle = computed(() => {
      return {
        display: 'grid',
        ...getGridStyle(props.grid)
      }
    })

    const internalAreas = computed((): Array<{ name: string, areaClass: string[], areaStyle: string, attrs: Record<string, any> }> => {
      return getAreaList(props.grid?.area ?? []).map(area => {
        const classes = ['sdv-grid-area', `sdv-grid-area__${area}`]
        const styles = `grid-area: ${area}`
        return {
          name: area,
          areaClass: classes,
          areaStyle: styles,
          attrs: {
            key: area,
            class: classes,
            style: styles
          }
        }
      })
    })

    function findElementByArea (area: string) {
      if (rootEl.value == null) throw new Error('rootEl is null')
      return rootEl.value.querySelector(`.sdv-grid-area__${area}`)
    }

    return {
      rootStyle,
      internalAreas,
      rootEl,
      findElementByArea
    }
  }
})
</script>

<style>

</style>
